/**
 * liveEntryTracking — les événements Amplitude de l'entrée dans le live que l'écran émet lui-même,
 * sous les noms et avec les propriétés de l'ancien `PreCallScreen` : les tableaux lisent ces chaînes
 * telles quelles, et Amplitude n'ingère plus de nom nouveau (plafond des types d'événements).
 *
 * Le reste de l'entrée se mesure ailleurs, déjà sous ses anciens noms : le clic vers la fiche de
 * l'app (`download_app.store_click`, `ui/hooks/openLiveAppStore`), le test du matériel
 * (`precall.opened`, `precall.step.verdict`, `precall.rung.*`, par la machine du moteur) et la
 * conversation de Sam (`sam_screen.open`, `.response.click`, `.feedback.submit`, `.close`, par
 * `useCheckpointConversation`).
 */
import * as amplitude from "@amplitude/analytics-react-native"

import { joinTracking } from "../../../../../../engine/precall/liveEntry"
import { PRECALL_EVENT, trackPrecall } from "../../../../../../engine/precall/precallTracking"

type DeviceCheckViewForJoin = Parameters<typeof joinTracking>[0]

/** Ce que chaque événement du test porte, quel que soit l'appareil (`live_session_id`, `platform`, `browser`). */
export type LiveEntryTrackingBase = Record<string, string>

/** `precall.join` : l'essai d'entrer et ce qui l'explique, qu'il soit entré ou reparti. */
export function trackEntryJoin(
    base: LiveEntryTrackingBase,
    view: DeviceCheckViewForJoin,
    entered: boolean,
    startingBlock: boolean
): void {
    trackPrecall(PRECALL_EVENT.JOIN, { ...base, ...joinTracking(view, entered, startingBlock) })
}

/** `precall.alert.sent` : le signalement silencieux est parti vers l'équipe. */
export function trackTechnicianAlert(base: LiveEntryTrackingBase, category: string): void {
    trackPrecall(PRECALL_EVENT.ALERT_SENT, { ...base, category })
}

/**
 * « C'est parti ! » au bout du starting block, dans l'ordre d'avant : l'événement de l'écran de Sam,
 * le geste (la fermeture, qui émet `sam_screen.close`), puis le bouton suivi (`FilledButtonM
 * trackingId="starting_block_cest_parti"` émettait après son `onPress`).
 */
export function startingBlockGo(close: () => void): void {
    amplitude.track("sam_screen.starting_block.cest_parti.click")
    close()
    amplitude.track("button.clicked.starting_block_cest_parti")
}
